/**
 * Just enough Markdown for what a chat reply actually contains.
 *
 * The panel printed raw text, so a reply with headings and bullets arrived as
 * literal `###` and `-`. Pulling in a full Markdown stack for a side panel is a
 * lot of bundle for four constructs, and rendering to HTML would put model
 * output into the DOM as markup. This parses to plain data instead: the renderer
 * builds React elements from it, so nothing the model writes can become markup.
 *
 * Anything it does not recognise stays as text — the failure mode is a line that
 * looks slightly plain, never a broken or unsafe one.
 */

export type Span = { text: string; bold?: boolean; code?: boolean };

export type Block =
  | { type: 'heading'; spans: Span[] }
  | { type: 'paragraph'; spans: Span[] }
  | { type: 'bullet'; spans: Span[] }
  | { type: 'numbered'; index: number; spans: Span[] };

/** Splits `**bold**` and `` `code` `` out of a line, leaving the rest as text. */
export function parseInline(line: string): Span[] {
  const spans: Span[] = [];
  // One pass over both markers so they cannot nest into each other wrongly.
  const pattern = /\*\*([^*]+)\*\*|`([^`]+)`/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(line)) !== null) {
    if (match.index > last) spans.push({ text: line.slice(last, match.index) });
    if (match[1] !== undefined) spans.push({ text: match[1], bold: true });
    else spans.push({ text: match[2], code: true });
    last = match.index + match[0].length;
  }
  if (last < line.length) spans.push({ text: line.slice(last) });

  return spans.length > 0 ? spans : [{ text: line }];
}

export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  // A blank line is the model's paragraph break. Skipping it outright merged two
  // separate thoughts into one run-on paragraph.
  let broke = true;

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trimEnd();
    if (line.trim() === '') {
      broke = true;
      continue;
    }

    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ type: 'heading', spans: parseInline(heading[1]) });
      broke = true;
      continue;
    }

    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    if (bullet) {
      blocks.push({ type: 'bullet', spans: parseInline(bullet[1]) });
      broke = true;
      continue;
    }

    const numbered = /^\s*(\d+)[.)]\s+(.*)$/.exec(line);
    if (numbered) {
      blocks.push({
        type: 'numbered',
        index: Number(numbered[1]),
        spans: parseInline(numbered[2]),
      });
      broke = true;
      continue;
    }

    // Consecutive plain lines belong to the same paragraph; the model wraps mid
    // sentence and re-splitting every line would double the vertical space.
    const previous = blocks[blocks.length - 1];
    if (!broke && previous?.type === 'paragraph') {
      previous.spans.push({ text: ' ' }, ...parseInline(line));
    } else {
      blocks.push({ type: 'paragraph', spans: parseInline(line) });
    }
    broke = false;
  }

  return blocks;
}
